import { displayGroupLabel, isVendorBookableRole } from '../engines/lineRoles';
import { buildPayrollIsLineKey } from '../engines/payrollKeys';
import { groupIdOf, templateGroupLabels } from '../utils/seatDepartments';

/* ═══════════════════════════════════════════════════════════════════════════════
   SHARED IS-LINE TAXONOMY + CASCADING PICKER

   Extracted VERBATIM from VendorDashboard.js (Path C Session 5b grouping (ii)) so
   the Vendor Modal AND the Scenario Analysis new-vendor form drive their IS-line
   dropdown from ONE component — no divergent lookalike copies. Sourced entirely
   from per-tenant rawData.glMappings (statement_type='IS'); the prior module-scope
   hardcoded Semitek slug lists silently misclassified non-Semitek tenants (e.g.
   AE Model's 34 per-line is_line values → computeVendor.js:108 coverage-gate drop).

   The vendor-assignable rule is engines/lineRoles.js isVendorBookableRole (W-002).
   ═══════════════════════════════════════════════════════════════════════════════ */
/* W-002 (2026-09-24): WAGES_IS_LINES and PAYROLL_ROLES are RETIRED -- isVendorAssignable now reads the ONE
   vendor-bookable rule in engines/lineRoles.js (a line the vendor engine books), which also excludes the D&A
   and revenue lines the old role set + 5 keys let through. */


// Section color palette keyed by is_category (cid=1/cid=2 use Revenue/COGS/S&M/G&A/R&D/Other;
// cid=3 lacks Revenue). Unknown categories fall back to gray.
export const SECTION_COLOR = {
  "Revenue": "#8E44AD",
  "COGS":    "#C0392B",
  "S&M":     "#2E86C1",
  "G&A":     "#27AE60",
  "R&D":     "#B7950B",
  "Other":   "#7F8C8D",
};
// Canonical IS section ordering; unknown categories sort to the end alphabetically.
export const SECTION_ORDER = ["Revenue", "COGS", "S&M", "G&A", "R&D", "Other"];

// Pure builder — moved verbatim from the VendorDashboard `isLineTaxonomy` useMemo body.
// Drives all in-page consumers (modal dropdown, computeVendors bucketing, IS Summary,
// Prepaid catLabel, All Vendors filter/sort/render, Dashboard By Section) from
// per-tenant gl_line_mappings.
export function buildISLineTaxonomy(glMappings) {
  const glmIS = (glMappings || []).filter(g => g.statement_type === "IS");
  const sectionOrderIdx = (s) => {
    const i = SECTION_ORDER.indexOf(s);
    return i >= 0 ? i : SECTION_ORDER.length;
  };
  const sectionsSeen = new Set();
  glmIS.forEach(g => { if (g.is_category) sectionsSeen.add(g.is_category); });
  const sections = [...sectionsSeen]
    .sort((a, b) => sectionOrderIdx(a) - sectionOrderIdx(b) || a.localeCompare(b))
    .map(s => ({ id: s, label: s, color: SECTION_COLOR[s] || "#7F8C8D" }));
  const entries = glmIS
    .filter(g => g.is_line_key && g.is_category)
    .map(g => ({
      id: g.is_line_key,
      label: g.display_name || g.gl_name || g.is_line_key,
      section: g.is_category,
      displayGroup: g.display_group || null,
      sortOrder: g.sort_order ?? 9999,
      role: g.role || null,
      isVendorAssignable: isVendorBookableRole(g.role),
    }));
  const byKey = new Map();
  entries.forEach(e => byKey.set(e.id, e));
  const bySection = new Map();
  sections.forEach(s => bySection.set(s.id, []));
  entries.forEach(e => {
    const bucket = bySection.get(e.section);
    if (bucket) bucket.push(e);
  });
  bySection.forEach(arr => arr.sort((a, b) =>
    a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)
  ));
  const ordered = [];
  sections.forEach(s => {
    const arr = bySection.get(s.id) || [];
    arr.forEach(e => ordered.push(e));
  });
  const indexMap = new Map();
  ordered.forEach((e, i) => indexMap.set(e.id, i));
  const sectionById = new Map();
  sections.forEach(s => sectionById.set(s.id, s));
  return { sections, entries, ordered, byKey, bySection, indexMap, sectionById };
}

// First vendor-assignable entry id — the "+ Add Vendor" default (was hardcoded
// 'ga_software' pre-refactor; AE Model doesn't have that key).
export function defaultVendorIsLine(taxonomy) {
  return taxonomy?.entries?.find(e => e.isVendorAssignable)?.id || "";
}

const PAYTYPE_GREY_LABEL = { wages: "Salaries", bonuses: "Bonuses", payroll_taxes: "Payroll Taxes", benefits: "Benefits" };
const IS_CAT_SLUG = { COGS: "cogs", "S&M": "sm", "G&A": "ga", "R&D": "rd" };

// Synthesize the per-display_group PAYROLL leaves that live as RUNTIME dynPayrollLeaves
// (buildISLines) and are therefore ABSENT from gl_line_mappings — so a multi-dg section
// (e.g. AE Model S&M: sm_sales_mktg_wages, sm_client_success_wages, …) shows NO payroll
// rows in the picker while single-dg sections (COGS/G&A/R&D, whose catch-all payroll rows
// DO live in glMappings) show them greyed. Returns { <is_category>: [greyed entries] } to
// be MERGED into the picker dropdown for DISPLAY ONLY — NOT added to the taxonomy object,
// so the taxonomy's other consumers (IS Summary, By Section, computeVendors) are unchanged.
// Skips keys already present in the taxonomy (catch-all glLeaves already shown/greyed) and
// returns {} for legacy tenants (no department_mappings → no dynPayroll).
export function buildPayrollGreyedEntries(deptMappings, taxonomy) {
  const dms = deptMappings || [];
  if (!dms.length || !taxonomy) return {};
  const bySection = {};
  const seen = new Set();
  dms.forEach(dm => {
    const catSlug = IS_CAT_SLUG[dm.is_category];
    if (!catSlug) return;
    const dg = dm.display_group || catSlug;
    ["wages", "bonuses", "payroll_taxes", "benefits"].forEach(pt => {
      const key = buildPayrollIsLineKey(catSlug, dg, pt);
      if (seen.has(key)) return;
      seen.add(key);
      if (taxonomy.byKey.has(key)) return; // already a glLeaf (greyed via role) → don't duplicate
      (bySection[dm.is_category] = bySection[dm.is_category] || []).push({
        id: key, label: PAYTYPE_GREY_LABEL[pt], section: dm.is_category,
        displayGroup: dg, isVendorAssignable: false, synthetic: true,
      });
    });
  });
  return bySection;
}

// Cascading section→line picker — moved verbatim from the VendorModal IS-Category
// dropdown (VendorDashboard.js). `style` is the caller's input style; `value` is the
// current is_line_key; `onChange(id)` receives the newly-selected vendor-assignable id.
export function ISLinePicker({ taxonomy, value, onChange, style, extraGreyed }) {
  const currentEntry = taxonomy.byKey.get(value);
  const currentSection = currentEntry?.section
    || taxonomy.sections[0]?.id || "";
  // Merge display-only greyed entries (runtime dynPayroll leaves absent from glMappings)
  // into the current section so multi-dg sections show their payroll lines greyed. These
  // are never assignable (disabled) and are NOT in the taxonomy object.
  const baseEntries = taxonomy.bySection.get(currentSection) || [];
  const extras = (extraGreyed && extraGreyed[currentSection]) || [];
  const sectionEntries = extras.length ? [...baseEntries, ...extras] : baseEntries;
  const nonAssignableExtra = new Set(extras.map(e => e.id));
  // Group section entries by displayGroup when any entry in the section carries a
  // populated display_group (e.g. AE Model S&M post-Session-4f). Flat list otherwise.
  const hasDg = sectionEntries.some(e => e.displayGroup);
  // 2026-10-28: a template group TEXT ("Sales") is the group deptSlug(text) -- one optgroup with the payroll lines' group, under its
  // own text. {} when every group is a slug.
  const textLabels = templateGroupLabels(sectionEntries.map(e => e.displayGroup), []);
  const dgBuckets = (() => {
    if (!hasDg) return null;
    const groups = new Map();
    sectionEntries.forEach(e => {
      const k = groupIdOf(e.displayGroup) || "__unassigned__";
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(e);
    });
    return [...groups.entries()];
  })();
  return <>
    <select style={style} value={currentSection} onChange={e => {
      const newSec = e.target.value;
      const newSecEntries = taxonomy.bySection.get(newSec) || [];
      // Pick the first vendor-assignable entry as the new isCat; if every entry in
      // the new section is disabled, fall back to the very first entry so the
      // dropdown stays consistent.
      const next = newSecEntries.find(e2 => e2.isVendorAssignable)
                || newSecEntries[0];
      if (next) onChange(next.id);
    }}>
      {taxonomy.sections.map(s =>
        <option key={s.id} value={s.id}>{s.label}</option>
      )}
    </select>
    <select style={{ ...style, marginTop: "6px" }} value={value} onChange={e => {
      const v = e.target.value;
      const entry = taxonomy.byKey.get(v);
      if ((entry && !entry.isVendorAssignable) || nonAssignableExtra.has(v)) return;
      onChange(v);
    }}>
      {hasDg
        ? dgBuckets.map(([dg, entries]) => (
            <optgroup key={dg} label={dg === "__unassigned__" ? "(unassigned)" : (textLabels[dg] || displayGroupLabel(dg))}>
              {entries.map(e => {
                const dis = !e.isVendorAssignable;
                return <option key={e.id} value={e.id} disabled={dis} style={dis ? { color: "#9CA3AF" } : undefined}>
                  {dis ? `${e.label} (not available for vendors)` : e.label}
                </option>;
              })}
            </optgroup>
          ))
        : sectionEntries.map(e => {
            const dis = !e.isVendorAssignable;
            return <option key={e.id} value={e.id} disabled={dis} style={dis ? { color: "#9CA3AF" } : undefined}>
              {dis ? `${e.label} (not available for vendors)` : e.label}
            </option>;
          })}
    </select>
    <div style={{ fontSize: "10px", color: "#8B8FA3", marginTop: "4px", fontFamily: "'Space Mono',monospace" }}>
      → {value || "(none)"}
    </div>
  </>;
}
